import { useEffect, useState } from 'react';
import { gql, useQuery } from '@apollo/client';
import { useCurrentMemberContext } from 'src/contexts';
import { useGetProjectsByGantt } from './get-projects-by-gantt.hook';

export const GET_PROJECTS_BY_RANGE = gql`
  query getProjectsByRange($payload: GetProjectsByDateInput!) {
    getProjectsByRange(payload: $payload) {
      collection {
        _id
        name
        projectNumber
        details
        startDate
        createdAt
        createdBy
        status
        ganttIds
        plannedHours
        gantts {
          name
        }
        address {
          street
          city
          country
          zipCode
        }
        customerId
        companyId
        customer {
          name
          deleted
        }
        ownerMemberId
        ownerMember {
          profile {
            firstName
            lastName
          }
        }
        deputyMemberId
        deputyMember {
          profile {
            firstName
            lastName
          }
        }
        tasks {
          _id
          name
          notes
          plannedHours
          files {
            _id
            location
            filename
            key
          }
          timeStripes {
            _id
            name
            plannedHours
            startDate
            ganttId
            gantt {
              name
            }
          }
        }
      }
      totalCount
    }
  }
`;

const transformProjects = (project: {
  _id: any;
  projectNumber: any;
  name: any;
  customer: { name: any };
  customerId: any;
  ownerMember: { profile: { firstName: string; lastName: string } };
  plannedHours: any;
}) => ({
  projectId: project._id,
  projectNumber: project.projectNumber,
  projectName: project.name,
  projectCustomer: project.customer.name,
  projectCustomerId: project.customerId,
  projectOwner: project.ownerMember.profile.firstName + ' ' + project.ownerMember.profile.lastName,
  projectPlannedHours: project.plannedHours,
});

const getSortingField = (sorterField: string) => {
  let field: string;

  switch (sorterField) {
    case 'projectNumber':
      field = 'projectNumber';
      break;

    case 'projectName':
      field = 'name';
      break;

    case 'projectCustomer':
      field = 'customer.name';
      break;

    case 'projectOwner':
      field = 'ownerMember.profile.firstName';
      break;

    default:
      field = 'createdAt';
  }

  return field;
};

export const useGetProjectsByDate = (startDate: any, endDate: any, dateRange: string) => {
  const [projectsIds, setProjectsIds] = useState<string[]>([]);
  const [projects, setProjects] = useState<any[]>([]);

  const [prevDateRange, setPrevDateRange] = useState(dateRange);

  const [currentPage, setPage] = useState(1);

  const [currentNameSort, setNameSort] = useState<{ field: string; sortBy: number }[]>();
  const [isSorted, setIsSorted] = useState(false);

  const { ganttId } = useCurrentMemberContext();

  const [prevGanttId, setPrevGanttId] = useState(ganttId);

  const resetProjectsList = () => {
    setProjects([]);
    setPage(1);
  };

  const pageSize = 5;

  const { projects: projectsData } = useGetProjectsByGantt(ganttId);

  const total = projectsData.length;

  useEffect(() => {
    if (total > 0) {
      projectsData.forEach((project: { _id: any }) => {
        setProjectsIds(prev => [...prev, project._id]);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total, ganttId]);

  useEffect(() => {
    setPage(1);
  }, []);

  useEffect(() => {
    if (ganttId !== prevGanttId) {
      setProjectsIds([]);
      resetProjectsList();
    }
  }, [ganttId, prevGanttId]);

  useEffect(() => {
    if (dateRange !== prevDateRange) {
      resetProjectsList();
    }
  }, [dateRange, prevDateRange]);

  const { data, loading } = useQuery(GET_PROJECTS_BY_RANGE, {
    variables: {
      payload: {
        projectsIds,
        startDate,
        endDate,
        queryOptions: {
          limit: pageSize,
          sort: currentNameSort,
          skip: pageSize * currentPage - pageSize,
        },
      },
    },
    skip: !projectsIds.length,
    fetchPolicy: 'network-only',
  });

  const { collection = [], totalCount = 0 } = data?.getProjectsByRange || {};

  useEffect(() => {
    if (totalCount > 0) {
      collection.forEach(
        (project: {
          ganttIds: any;
          projectNumber: any;
          name: any;
          customer: { name: any };
          customerId: any;
          ownerMember: { profile: { firstName: string; lastName: string } };
          plannedHours: any;
          _id: any;
        }) => {
          setProjects(prev => [...prev, transformProjects(project)]);
          setPrevGanttId(ganttId);
          setPrevDateRange(dateRange);
        }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalCount]);

  return {
    loading,
    projects,
    setPage,
    paginationConfig: {
      total: totalCount,
      pageSize,
      current: currentPage,
      onChange: (page: number) => setPage(page),
    },
    onTableChange: (a: any, b: any, sorter: any) => {
      if (sorter.field) {
        setIsSorted(!isSorted);

        const sortBy = isSorted ? -1 : 1;

        setNameSort([
          {
            field: getSortingField(sorter.field),
            sortBy,
          },
        ]);

        resetProjectsList();
      }
    },
  };
};
